<template>
  <div class="divide-ontick-steel-500 flex flex-col divide-y">
    <div class="grid grid-cols-7 py-2">
      <div
        v-for="item in header"
        :key="item"
        class="flex items-center justify-center text-gray-400"
      >
        {{ item }}
      </div>
    </div>
    <div class="grid grid-cols-7 space-y-1 py-2">
      <button
        v-for="day in days"
        :key="`${day.value}-${day.weekDay}`"
        class="flex items-center justify-center py-1.5 disabled:cursor-not-allowed"
        :disabled="isDisabled(day)"
        :class="styles(day)"
        @click="selectDate(day)"
      >
        {{ day.visible ? day.value : "" }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    displayedDate: {
      type: Object,
    },
    selectedDate: {
      type: Object,
    },
    showAdjacentMonthDays: {
      type: Boolean,
      default: false,
    },
    allowWeekends: {
      type: Boolean,
      default: false,
    },
    allowPast: {
      type: Boolean,
      default: false,
    },
    allowFuture: {
      type: Boolean,
      default: true,
    },
    range: {
      type: Boolean,
    },
  },

  computed: {
    header() {
      return ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    },
    daysInMonth() {
      return this.moment(this.displayedDate).daysInMonth();
    },
    startingDayOfMonth() {
      return this.moment(this.displayedDate).startOf("month").day();
    },
    endingDayOfMonth() {
      return this.moment(this.displayedDate).endOf("month").day();
    },
    daysInLastMonth() {
      return this.moment(this.displayedDate)
        .subtract(1, "months")
        .daysInMonth();
    },
    daysInNextMonth() {
      return this.moment(this.displayedDate).add(1, "months").daysInMonth();
    },
    currentDate() {
      return this.moment();
    },
    currentMonthNumberedDays() {
      let arr = [];
      let currentDate = this.currentDate;
      for (let day = 1; day <= this.daysInMonth; day++) {
        arr.push({
          value: day,
          isCurrentMonth: true,
          weekDay: this.moment(this.displayedDate).date(day).day(),
          visible: true,
          isPast: this.moment(currentDate).isAfter(
            this.moment(this.displayedDate).date(day),
            "day"
          ),
          isFuture: this.moment(currentDate).isBefore(
            this.moment(this.displayedDate).date(day),
            "day"
          ),
        });
      }
      return arr;
    },
    lastMonthNumberedDays() {
      let arr = [];
      for (let i = 1; i <= this.daysInLastMonth; i++) {
        arr.push({
          value: i,
          isCurrentMonth: false,
          weekDay: null,
          visible: this.showAdjacentMonthDays,
        });
      }
      return arr.slice(
        this.daysInLastMonth - this.startingDayOfMonth,
        this.daysInLastMonth + 1
      );
    },
    nextMonthNumberedDays() {
      let arr = [];
      for (let i = 1; i <= this.daysInNextMonth; i++) {
        arr.push({
          value: i,
          isCurrentMonth: false,
          weekDay: null,
          visible: this.showAdjacentMonthDays,
        });
      }
      if (this.endingDayOfMonth === 6) return [];
      return arr.slice(0, this.startingDayOfMonth);
    },
    days() {
      return [
        ...this.lastMonthNumberedDays,
        ...this.currentMonthNumberedDays,
        ...this.nextMonthNumberedDays,
      ];
    },
  },

  methods: {
    isWeekend(weekday) {
      return [0, 6].includes(weekday);
    },
    isDisabled(day) {
      if (
        (!this.allowWeekends && this.isWeekend(day.weekDay)) ||
        (!this.allowPast && day.isPast) ||
        (!this.allowFuture && day.isFuture) ||
        !day.isCurrentMonth
      ) {
        return true;
      }
      return false;
    },
    selectDate(day) {
      this.$emit(
        "select-date",
        this.moment(this.displayedDate).date(day.value)
      );
    },
    isSelected(day) {
      let start = this.selectedDate.start;
      let end = this.selectedDate.end;
      return (
        ((!!start &&
          this.moment(this.displayedDate)
            .date(day.value)
            .isSame(start, "day")) ||
          (!!end &&
            this.moment(this.displayedDate)
              .date(day.value)
              .isSame(end, "day"))) &&
        day.isCurrentMonth
      );
    },
    isWithinRange(day) {
      if (this.range) {
        const startExists = Boolean(this.selectedDate?.start);
        const endExists = Boolean(this.selectedDate?.end);
        if (startExists && endExists) {
          return (
            this.moment(this.displayedDate)
              .date(day.value)
              .isBetween(this.selectedDate.start, this.selectedDate.end) &&
            day.isCurrentMonth
          );
        }
      }
      return false;
    },
    isStart(day) {
      return (
        this.isSelected &&
        this.moment(this.selectedDate.start).isSame(
          this.moment(this.displayedDate).date(day.value),
          "day"
        )
      );
    },
    isEnd(day) {
      return (
        this.isSelected &&
        this.moment(this.selectedDate.end).isSame(
          this.moment(this.displayedDate).date(day.value),
          "day"
        )
      );
    },
    fontWeight(day) {
      if (this.isDisabled(day)) {
        return "font-normal";
      }
      if (this.isWithinRange(day)) {
        return "font-semibold";
      }
      if (this.isSelected(day)) {
        return "font-bold";
      }
      return "font-normal";
    },
    styles(day) {
      let arr = [];
      if (this.isDisabled(day)) {
        arr.push("text-gray-500");
      }
      if (
        day.visible &&
        !this.isDisabled(day) &&
        !this.isWithinRange(day) &&
        !this.isSelected(day)
      ) {
        arr.push("transition-all hover:bg-ontick-blue-800 rounded");
      }
      if (
        this.isWithinRange(day) &&
        !this.isDisabled(day) &&
        !this.isSelected(day)
      ) {
        arr.push("bg-ontick-blue-800");
      }
      if (this.isSelected(day)) {
        arr.push("bg-ontick-blue-600");
      }
      if (this.isStart(day)) {
        arr.push("rounded-l");
      }
      if (this.isEnd(day) || (this.isStart(day) && !this.selectedDate.end)) {
        arr.push("rounded-r");
      }
      arr.push(this.fontWeight(day));

      return arr.join(" ");
    },
  },
};
</script>

<style></style>
